import type { LibraryEntry } from "../api/contract";
import type { SectionSummary } from "../components/PreviewFrame";

export interface SectionGap {
  above: SectionSummary;
  below: SectionSummary;
  top: number;
  left: number;
}

/** The insertion targets between the ordered sections reported by the preview bridge. */
export function sectionGaps(sections: SectionSummary[]): SectionGap[] {
  return sections.slice(0, -1).map((above, index) => {
    const below = sections[index + 1]!;
    const overlapLeft = Math.max(above.rect.left, below.rect.left);
    const overlapRight = Math.min(above.rect.right, below.rect.right);
    const left = overlapRight >= overlapLeft
      ? overlapLeft + (overlapRight - overlapLeft) / 2
      : above.rect.left + above.rect.width / 2;
    return { above, below, top: above.rect.bottom + (below.rect.top - above.rect.bottom) / 2, left };
  });
}

/** Collections which do not already have a bound entry on the current page. */
export function insertCollections(entries: LibraryEntry[], sections: SectionSummary[]): Array<{ id: string; label: string }> {
  const pageBinds = new Set(sections.flatMap((section) => section.binds).map((bind) => bind.split(".")[0]));
  const groups = new Map<string, LibraryEntry[]>();
  entries.filter((entry) => entry.kind !== "media").forEach((entry) => {
    const collection = entry.list?.collectionId ?? entry.kind;
    groups.set(collection, [...(groups.get(collection) ?? []), entry]);
  });
  return [...groups]
    .filter(([, collection]) => !collection.some((entry) => pageBinds.has(entry.id)))
    .map(([id, collection]) => {
      const kind = collection[0]!.kind;
      return { id, label: kind === "faq" ? "Questions" : `${kind[0]!.toUpperCase()}${kind.slice(1)}s` };
    });
}

export function addSectionNote(pick: string): string {
  const label = pick.trim();
  return `Add ${/^(a|an)\s/i.test(label) ? "" : "a "}${label} section below this.`;
}
